export type AuthUser = { username: string; role?: string; name?: string; email?: string };

export const AUTH_CACHE_KEY = 'auth:user';
const AUTH_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export function getCachedAuthUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    const ts = typeof obj?.ts === 'number' ? obj.ts : 0;
    if (Date.now() - ts > AUTH_CACHE_TTL_MS) { window.localStorage.removeItem(AUTH_CACHE_KEY); return null; }
    if (obj && obj.user && typeof obj.user.username === 'string') return obj.user as AuthUser;
    return null;
  } catch { return null; }
}

// BroadcastChannel for cross-tab/component auth sync
const AUTH_BROADCAST_CHANNEL = 'auth:channel';
let authChannel: BroadcastChannel | null = null;
function getAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof (window as any).BroadcastChannel === 'undefined') return null;
  if (!authChannel) authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  return authChannel;
}

export function setCachedAuthUser(user: AuthUser | null) {
  if (typeof window === 'undefined') return;
  try {
    if (user) window.localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ user, ts: Date.now() }));
    else window.localStorage.removeItem(AUTH_CACHE_KEY);
    // Broadcast update so other tabs/components can react without hitting server
    try { getAuthChannel()?.postMessage({ type: 'auth', user }); } catch {}
  } catch { /* ignore */ }
}

export function subscribeAuth(handler: (user: AuthUser | null) => void): () => void {
  const ch = getAuthChannel();
  if (!ch) return () => {};
  const onMsg = (ev: MessageEvent) => {
    const msg = ev.data || {};
    if (msg && msg.type === 'auth') handler((msg.user as AuthUser) || null);
  };
  ch.addEventListener('message', onMsg as any);
  return () => { try { ch.removeEventListener('message', onMsg as any); } catch {} };
}

const BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

async function tryFetch(path: string, init?: RequestInit) {
  let last: any = null;
  for (const b of BASES) {
    const url = b ? `${b}${path}` : path;
    try {
      const res = await fetch(url, { credentials: 'include', ...(init || {}) });
      if (res.ok) return res;
      last = new Error(`HTTP ${res.status}`);
    } catch (e) { last = e; }
  }
  throw last || new Error('Network error');
}

async function parseJson(res: Response) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) return await res.json();
  const text = await res.text().catch(() => '');
  const snippet = text ? text.slice(0, 200) : '';
  throw new Error(`Expected JSON but got ${ct || 'unknown content-type'}${snippet ? `: ${snippet}` : ''}`);
}

// Like tryFetch but returns JSON even on non-2xx if possible
async function fetchJsonAcrossBases(path: string, init?: RequestInit): Promise<{ status: number; ok: boolean; json: any }> {
  let lastErr: any = null;
  for (const b of BASES) {
    const url = b ? `${b}${path}` : path;
    try {
      const res = await fetch(url, { credentials: 'include', ...(init || {}) });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('application/json')) {
        const json = await res.json().catch(() => ({}));
        return { status: res.status, ok: res.ok, json };
      }
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('Network error');
}

// ----- Auth status coalescing and short-term caching -----
type AuthStatusResp = { ok: boolean; user?: AuthUser };
let authInFlight: Promise<AuthStatusResp> | null = null;
let authCached: { ts: number; data: AuthStatusResp } | null = null;
const AUTH_STATUS_CACHE_MS = 2500; // small window to dedupe bursts

async function getThrottledAuthStatus(): Promise<AuthStatusResp> {
  const now = Date.now();
  if (authCached && now - authCached.ts < AUTH_STATUS_CACHE_MS) {
    return authCached.data;
  }
  if (authInFlight) return authInFlight;
  authInFlight = (async () => {
    try {
      const res = await tryFetch('/auth_status.php', { method: 'GET' });
      const data = (await parseJson(res)) as AuthStatusResp;
      authCached = { ts: Date.now(), data };
      return data;
    } finally {
      // release in the next tick to allow micro-batch of calls in same turn
      const p = Promise.resolve().then(() => { authInFlight = null; });
      await p;
    }
  })();
  return authInFlight;
}

export async function authLogin(username: string, password: string) {
  const { status, ok, json } = await fetchJsonAcrossBases('/auth_login.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (ok && json?.user) {
    try { setCachedAuthUser(json.user as AuthUser); } catch {}
    return json;
  }
  // Map 401 to a friendly credentials message
  if (status === 401) {
    return { ok: false, error: 'Credentials not correct' };
  }
  return { ok: false, error: json?.error || `HTTP ${status}` };
}

export async function authStatus(): Promise<{ ok: boolean; user?: AuthUser }> {
  // Coalesce concurrent requests and cache for a short window to prevent bursts
  try {
    return await getThrottledAuthStatus();
  } catch { return { ok: false }; }
}

export async function authLogout(): Promise<boolean> {
  try {
    const res = await tryFetch('/auth_logout.php', { method: 'POST' });
    const data = await parseJson(res);
    if (data.ok) setCachedAuthUser(null);
    return !!data.ok;
  } catch { return false; }
}
